/**
 * Centralized copy for the Customer Information list. Kept out of the
 * component per the repo rule that feature text is not hardcoded inline
 * (mirrors MemberBooksTab.strings.ts).
 */
export const customerInformationStrings = {
  total: {
    label: "Total customers: {count}",
    /** Shown in place of the count until the first response arrives. */
    pending: "…",
  },
  columns: {
    access: "Access",
    credits: "Credits",
    creditsHint: "Print-credit balance from the DS credits ledger",
    lastLogin: "Last login",
    lastLoginHint: "Time since the member last signed in",
    created: "Created",
  },
  /** Shown in a cell whose value DS did not return. */
  none: "—",
  filter: {
    accessLabel: "Filter by access",
    allAccess: "All access",
    active: "Active",
    inactive: "Inactive",
    pending: "Pending",
    lastLoginLabel: "Filter by last login",
    anyLastLogin: "Any last login",
    clear: "Clear filters",
  },
  sort: {
    /** Accessible name of a sortable header: what a click does next. */
    buttonLabel: "Sort by {column}, {direction}",
    ascending: "oldest first",
    descending: "most recent first",
  },
  legend: {
    title: "Last login",
    recent: "Last 7 days",
    stale: "8–30 days",
    old: "Over 30 days",
    never: "Never",
    noAccessHint: "Gray when the member has no access",
  },
  /**
   * Account-manager filter and assignment (abo-account-manager-assignment
   * task 04). Shared with the member detail, which shows the same select.
   */
  accountManager: {
    filterLabel: "Filter by account manager",
    all: "All account managers",
    notAssigned: "Not assigned",
    /** A member whose key is missing or matches no manager. */
    unknown: "-",
    inactiveName: "{name} (inactive)",
    selectLabel: "Account manager of {name}",
    updated: "Account manager updated",
    bulk: {
      label: "Assign all {count} to",
      managerLabel: "Account manager to assign",
      choose: "Choose a manager",
      assign: "Assign",
      tooLarge: "At most {max} members can be assigned at once. Narrow the filters.",
      confirmTitle: "Assign {count} members to {name}?",
      confirmBody: "Every member the current filters match gets this account manager.",
      cancel: "Cancel",
      assigning: "Assigning…",
      done: "{count} members assigned to {name}",
      countChanged: "The list changed; review it and try again",
    },
    refusal: {
      unverified: "Your sign-in could not be verified. Sign in again and retry.",
      forbidden: "You do not have permission to change account managers.",
      unavailable: "Account managers cannot be changed right now. Try again later.",
      notActive: "That manager is no longer active. Choose another one.",
      memberNotFound: "This member was not found.",
      generic: "Could not update the account manager. Please try again.",
    },
  },
  lastLogin: {
    never: "Never",
    today: "Today, {time}",
    yesterday: "Yesterday",
    daysAgo: "{count} days ago",
    monthsAgo: "{count} months ago",
    exactTooltip: "Last login: {date}",
    neverTooltip: "This member has never logged in",
  },
};

/** Replaces each `{key}` in `template` with its value. */
export const formatMessage = (
  template: string,
  values: Record<string, string | number>,
): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
